/**
 * Dynamics Theme System - Accent Color Utilities
 *
 * HSL-based accent color management for theme-dynamics
 * Allows dynamic accent color changes while maintaining accessibility
 */

/**
 * Apply HSL accent color to CSS variables
 * @param h Hue (0-360)
 * @param s Saturation (0-100)
 * @param l Lightness (0-100)
 */
export function applyAccentColor(h: number, s: number, l: number): void {
  // Store raw numeric values - CSS calc handles the % conversion
  document.documentElement.style.setProperty('--accent-h', h.toString());
  document.documentElement.style.setProperty('--accent-s', s.toString());
  document.documentElement.style.setProperty('--accent-l', l.toString());
}

/**
 * Get CSS variable value for accent color with optional opacity
 * @param opacity 0-1 (default 1)
 * @returns CSS hsl() string
 */
export function getAccentCSSValue(opacity: number = 1): string {
  const h = getComputedStyle(document.documentElement).getPropertyValue('--accent-h').trim();
  const s = getComputedStyle(document.documentElement).getPropertyValue('--accent-s').trim();
  const l = getComputedStyle(document.documentElement).getPropertyValue('--accent-l').trim();
  // Values are stored without % suffix, add it for hsl()
  return `hsl(${h}, ${s}%, ${l}%, ${opacity})`;
}

/**
 * Preset accent colors (Dynamics palette)
 * Inspired by Microsoft Fluent Design System
 */
export const ACCENT_PRESETS = [
  { name: 'Blue', h: 210, s: 79, l: 46 },
  { name: 'Teal', h: 180, s: 65, l: 42 },
  { name: 'Purple', h: 270, s: 60, l: 50 },
  { name: 'Orange', h: 30, s: 85, l: 55 },
  { name: 'Green', h: 140, s: 60, l: 45 },
  { name: 'Pink', h: 330, s: 70, l: 60 },
] as const;

export type AccentPreset = typeof ACCENT_PRESETS[number];
